import { useState } from 'react';
import { AlertCircle, ArrowDownLeft, ArrowUpRight, Check, CheckCircle2, ChevronDown, ChevronRight, Loader2, RotateCcw, Zap } from 'lucide-react';
import { PageShell } from '@/components/layout/PageShell';
import { EmptyState } from '@/components/ui/EmptyState';
import { useReconciliation, type PendingTx } from '@/hooks/useReconciliation';
import { formatBRL, formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';

// ─── Painel de candidatos ─────────────────────────────────────────────────────

function CandidatePanel({
  tx,
  onReconcile,
  reconciling,
}: {
  tx: PendingTx;
  onReconcile: (targetId: string, kind: 'payable' | 'receivable') => void;
  reconciling: boolean;
}) {
  const { suggestMatches } = useReconciliation();
  const candidates = suggestMatches(tx);

  if (candidates.length === 0) {
    return (
      <div className="p-4 text-sm text-ink-muted">
        Nenhum candidato com score ≥ 30%. Verifique se há títulos cadastrados em "A pagar" ou "A receber".
      </div>
    );
  }

  return (
    <div className="divide-y divide-border">
      {candidates.map((c) => (
        <div key={c.target_id} className="p-4 hover:bg-surface-subtle/40">
          <div className="flex items-center justify-between gap-3 mb-2">
            <div className="flex items-center gap-2">
              <div className={cn(
                'w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold tabular-nums',
                c.score >= 0.8 ? 'bg-positive/10 text-positive' :
                c.score >= 0.5 ? 'bg-warning/10 text-warning' : 'bg-border text-ink-muted'
              )}>
                {Math.round(c.score * 100)}%
              </div>
              <div>
                <p className="text-xs font-semibold text-ink uppercase tracking-wide">
                  {c.target_kind === 'payable' ? 'A pagar' : 'A receber'}
                </p>
                <p className="text-xs text-ink-muted">{c.target_id.slice(0, 8)}…</p>
              </div>
            </div>
            <button
              onClick={() => onReconcile(c.target_id, c.target_kind)}
              disabled={reconciling}
              className="btn-primary !py-1.5 !text-xs gap-1.5 shrink-0"
            >
              {reconciling ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
              Conciliar
            </button>
          </div>
          <div className="flex flex-wrap gap-1">
            {c.reasons.map((r) => (
              <span key={r} className="chip bg-surface-subtle text-ink-muted text-[11px]">{r}</span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Linha de transação ───────────────────────────────────────────────────────

function TxRow({
  tx,
  expanded,
  onToggle,
  onReconcile,
  onUnreconcile,
  reconciling,
}: {
  tx: PendingTx;
  expanded: boolean;
  onToggle: () => void;
  onReconcile: (targetId: string, kind: 'payable' | 'receivable') => void;
  onUnreconcile: () => void;
  reconciling: boolean;
}) {
  const isIn = tx.direction === 'in';
  return (
    <div className="border-b border-border last:border-0">
      <div
        className="flex items-center gap-3 px-4 py-3 hover:bg-surface-subtle/40 cursor-pointer"
        onClick={onToggle}
      >
        <span className={cn('w-6 h-6 rounded-lg flex items-center justify-center shrink-0',
          isIn ? 'bg-positive/10 text-positive' : 'bg-negative/10 text-negative')}>
          {isIn ? <ArrowDownLeft className="w-3.5 h-3.5" /> : <ArrowUpRight className="w-3.5 h-3.5" />}
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-ink truncate">{tx.description}</p>
          <p className="text-xs text-ink-muted">{formatDate(tx.occurred_at)} · {tx.account_name}</p>
        </div>
        <span className={cn('tabular-nums text-sm font-semibold shrink-0',
          isIn ? 'text-positive' : 'text-negative')}>
          {isIn ? '+' : '−'}{formatBRL(tx.amount_cents)}
        </span>
        {tx.reconciled ? (
          <button
            onClick={(e) => { e.stopPropagation(); onUnreconcile(); }}
            className="btn-ghost !p-1.5 text-ink-muted hover:text-negative shrink-0"
            title="Desfazer conciliação"
          >
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
        ) : (
          <span className="text-ink-muted shrink-0">
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </span>
        )}
      </div>
      {expanded && !tx.reconciled && (
        <div className="bg-surface-subtle/30 border-t border-border ml-9">
          <CandidatePanel tx={tx} onReconcile={onReconcile} reconciling={reconciling} />
        </div>
      )}
    </div>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────────

export function Reconciliation() {
  const {
    pendingTxs, allTxs, loading, reconciling,
    suggestMatches, reconcile, unreconcile, bulkReconcile,
  } = useReconciliation();

  const [expandedId, setExpandedId]     = useState<string | null>(null);
  const [showAll, setShowAll]           = useState(false);
  const [error, setError]               = useState('');
  const [bulkResult, setBulkResult]     = useState<{ success: number; errors: string[] } | null>(null);
  const [bulkLoading, setBulkLoading]   = useState(false);

  const displayed = showAll ? allTxs : pendingTxs;

  // Auto-conciliar: pares com score ≥ 0.85
  async function handleAutoReconcile() {
    setBulkLoading(true);
    setError('');
    setBulkResult(null);
    const pairs: Array<{ tx_id: string; target_id: string; target_kind: 'payable' | 'receivable' }> = [];
    for (const tx of pendingTxs) {
      const candidates = suggestMatches(tx);
      const best = candidates[0];
      if (best && best.score >= 0.85) {
        pairs.push({ tx_id: tx.id, target_id: best.target_id, target_kind: best.target_kind });
      }
    }
    if (pairs.length === 0) {
      setError('Nenhum match com confiança ≥ 85% encontrado.');
      setBulkLoading(false);
      return;
    }
    const result = await bulkReconcile(pairs);
    setBulkResult(result);
    setBulkLoading(false);
  }

  const highConfidenceCount = pendingTxs.filter((tx) => {
    const c = suggestMatches(tx)[0];
    return c && c.score >= 0.85;
  }).length;

  return (
    <PageShell
      title="Conciliação bancária"
      subtitle="Vincule transações importadas com títulos de contas a pagar e a receber."
      back="/financeiro"
      maxWidth="xl"
    >
      {/* Stat strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Não conciliadas', value: pendingTxs.length, color: 'text-warning' },
          { label: 'Conciliadas', value: allTxs.length - pendingTxs.length, color: 'text-positive' },
          { label: '% conciliado', value: allTxs.length ? `${Math.round((1 - pendingTxs.length / allTxs.length) * 100)}%` : '—', color: 'text-ink' },
          { label: 'Auto-conciliável (≥85%)', value: highConfidenceCount, color: 'text-brand-700' },
        ].map((s) => (
          <div key={s.label} className="card p-4">
            <p className="stat-label">{s.label}</p>
            <p className={cn('font-display font-bold text-2xl mt-1 tabular-nums', s.color)}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Ações */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <button
          onClick={handleAutoReconcile}
          disabled={bulkLoading || highConfidenceCount === 0}
          className="btn-primary gap-2"
        >
          {bulkLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
          Auto-conciliar {highConfidenceCount > 0 ? `(${highConfidenceCount})` : ''}
        </button>
        <label className="flex items-center gap-2 text-sm text-ink-muted cursor-pointer">
          <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} className="rounded" />
          Mostrar já conciliadas
        </label>
      </div>

      {/* Feedback */}
      {error && (
        <div className="rounded-xl border border-negative/30 bg-negative/5 p-3 text-sm text-negative flex items-center gap-2 mb-4">
          <AlertCircle className="w-4 h-4 shrink-0" /> {error}
        </div>
      )}
      {bulkResult && (
        <div className="rounded-xl border border-positive/30 bg-positive/5 p-3 text-sm text-positive flex items-center gap-2 mb-4">
          <CheckCircle2 className="w-4 h-4 shrink-0" />
          {bulkResult.success} conciliações feitas.
          {bulkResult.errors.length > 0 && ` ${bulkResult.errors.length} erros.`}
        </div>
      )}

      {/* Lista */}
      {loading ? (
        <div className="card p-8">
          <div className="space-y-3">
            {[1,2,3,4,5].map((i) => <div key={i} className="h-14 rounded-xl bg-surface-subtle animate-pulse" />)}
          </div>
        </div>
      ) : displayed.length === 0 ? (
        <div className="card p-8">
          <EmptyState
            icon={CheckCircle2}
            title={showAll ? 'Nenhuma transação' : 'Tudo conciliado!'}
            description={showAll ? 'Importe extratos para começar.' : 'Todas as transações foram conciliadas.'}
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          {/* Transações */}
          <div className="xl:col-span-2 card overflow-hidden">
            <div className="px-4 py-3 border-b border-border bg-surface-subtle/50">
              <p className="text-xs font-semibold text-ink-muted uppercase tracking-wider">
                {displayed.length} transação(ões) · clique para ver candidatos
              </p>
            </div>
            {displayed.map((tx) => (
              <TxRow
                key={tx.id}
                tx={tx}
                expanded={expandedId === tx.id}
                onToggle={() => setExpandedId(expandedId === tx.id ? null : tx.id)}
                onReconcile={async (targetId, kind) => {
                  try { await reconcile(tx.id, targetId, kind); setExpandedId(null); }
                  catch (e: any) { setError(e.message); }
                }}
                onUnreconcile={async () => {
                  try { await unreconcile(tx.id); }
                  catch (e: any) { setError(e.message); }
                }}
                reconciling={reconciling}
              />
            ))}
          </div>

          {/* Painel de instrução */}
          <div className="card p-5 h-fit">
            <h3 className="font-semibold text-ink mb-2">Como funciona</h3>
            <ol className="space-y-3 text-sm text-ink-muted list-decimal list-inside">
              <li>Clique em uma transação para ver os candidatos sugeridos</li>
              <li>O score indica a confiança do match (valor, data, descrição)</li>
              <li>Clique "Conciliar" para vincular a transação ao título</li>
              <li>Use "Auto-conciliar" para aprovar todos os matches com ≥ 85%</li>
            </ol>
            <div className="mt-4 space-y-2 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-8 h-5 rounded bg-positive/10 text-positive text-center font-bold text-[10px] flex items-center justify-center">80%</div>
                <span className="text-ink-muted">Alta confiança</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-5 rounded bg-warning/10 text-warning text-center font-bold text-[10px] flex items-center justify-center">50%</div>
                <span className="text-ink-muted">Média confiança — revisar</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-5 rounded bg-border text-ink-muted text-center font-bold text-[10px] flex items-center justify-center">30%</div>
                <span className="text-ink-muted">Baixa confiança</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
}
